<template>
  <section class="lead-flow-actions" aria-label="Workflow">
    <div class="lead-flow-actions__bar">
      <header class="lead-flow-actions__heading">
        <p>Workflow</p>
      </header>

      <div v-if="loading && !loaded" class="lead-flow-actions__loading" aria-label="Loading workflow">
        <span></span>
        <span></span>
      </div>
      <div v-else-if="error && !loaded" class="lead-flow-actions__state" role="alert">
        <p>Unable to load workflow actions.</p>
        <AppButton design="new" theme="dark" type="button" @click.native="loadFlow">Retry</AppButton>
      </div>
      <template v-else>
        <div
          v-if="isDecisionStage"
          class="lead-flow-actions__decision"
          :class="{ 'lead-flow-actions__decision--expired': decisionExpired }"
        >
          <div class="lead-flow-actions__decision-summary">
            <div class="lead-flow-actions__decision-heading">
              <AppStatusBadge
                design="new"
                :tone="decisionExpired ? 'red' : 'amber'"
                label="Make Decision"
              />
              <span>{{ decisionStateLabel }}</span>
            </div>
            <div class="lead-flow-actions__decision-timer" role="timer" aria-live="off">
              <strong>{{ decisionTimerLabel }}</strong>
              <small>Choose the next route for this lead</small>
            </div>
          </div>

          <div class="lead-flow-actions__decision-actions">
            <AppButton
              v-for="action in decisionActions"
              :key="action.code"
              design="new"
              class="lead-flow-actions__decision-action"
              :class="{
                'lead-flow-actions__decision-action--fast-track': action.code === 'fast_track_to_consult'
              }"
              :theme="action.code === 'start_first_contact' ? 'default' : 'dark'"
              type="button"
              :disabled="saving"
              @click.native="selectDecisionAction(action)"
            >
              <svg
                v-if="action.code === 'fast_track_to_consult'"
                class="lead-flow-actions__fast-track-icon"
                viewBox="0 0 24 24"
                fill="none"
                aria-hidden="true"
              >
                <path d="m12 3 1.35 4.15L17.5 8.5l-4.15 1.35L12 14l-1.35-4.15L6.5 8.5l4.15-1.35L12 3Z" />
                <path d="m18.5 14 .7 2.3 2.3.7-2.3.7-.7 2.3-.7-2.3-2.3-.7 2.3-.7.7-2.3Z" />
              </svg>
              {{ action.name }}
            </AppButton>
            <LeadFlowActionMenu
              v-if="exceptionalActions.length"
              label="More"
              :actions="exceptionalActions"
              :align-right="true"
              :show-trash-icon="true"
              @select="selectAction"
            />
          </div>
          <p
            v-if="formError && selectedAction && selectedAction.code === 'start_first_contact'"
            class="lead-flow-actions__decision-error"
            role="alert"
          >
            {{ formError }}
          </p>
        </div>
        <div v-else-if="visibleActions.length" class="lead-flow-actions__controls">
          <AppStatusBadge
            v-if="flow.current_stage"
            design="new"
            class="lead-flow-actions__stage"
            :color="flow.current_stage.color"
            :label="flow.current_stage.name"
          />
          <span class="lead-flow-actions__direction" aria-hidden="true">&rarr;</span>
          <div v-if="isNurtureStage" class="lead-flow-actions__inline-actions">
            <AppButton
              v-for="action in workflowActions"
              :key="action.code"
              design="new"
              type="button"
              :disabled="saving"
              @click.native="selectAction(action)"
            >
              {{ action.name }}
            </AppButton>
          </div>
          <div v-else class="lead-flow-actions__workflow-actions">
            <AppButton
              v-for="action in visibleActions"
              :key="action.code"
              design="new"
              :theme="recommendedAction && action.code === recommendedAction.code ? 'default' : 'dark'"
              type="button"
              :disabled="saving"
              @click.native="selectAction(action)"
            >
              {{ action.name }}
            </AppButton>
          </div>
        </div>
        <p v-else class="lead-flow-actions__empty">
          There are no available actions for this stage.
        </p>
      </template>
    </div>

    <LeadFlowActionForm
      v-if="selectedActionUsesForm"
      :action="selectedAction"
      :communication-methods="communicationMethods"
      :saving="saving"
      :server-errors="fieldErrors"
      :form-error="formError"
      @submit="submitAction"
      @cancel="cancelAction"
    />
  </section>
</template>

<script>
import { getLeadFlow, postLeadFlowAction } from '@/http'
import LeadFlowActionForm from '@/views/crm/task-tracker/LeadFlowActionForm.vue'
import LeadFlowActionMenu from '@/views/crm/task-tracker/LeadFlowActionMenu.vue'
import {
  createLeadContext,
  isActiveLeadContext
} from '@/views/crm/task-tracker/leadFlowContext'

const ACTIONS = {
  new_lead: [
    { code: 'start_first_contact', name: 'Need More Details', tone: 'primary', category: 'Decision' },
    {
      code: 'start_first_contact_manually',
      name: 'I will contact manually',
      tone: 'info',
      category: 'Decision'
    },
    {
      code: 'fast_track_to_consult',
      name: 'Move to Prepare Offer',
      tone: 'info',
      category: 'Decision'
    },
    { code: 'classify_duplicate', name: 'Mark as duplicate', tone: 'warning', category: 'Data quality' },
    { code: 'classify_spam', name: 'Mark as spam', tone: 'danger', category: 'Data quality' }
  ],
  first_contact: [
    { code: 'customer_replied', name: 'Customer replied', tone: 'primary', category: 'Contact recorded' },
    {
      code: 'fast_track_to_consult',
      name: 'Move to Consult',
      tone: 'info',
      category: 'Stage change',
      note_label: 'Reason',
      note_required: true,
      note_placeholder: 'Why can this lead move directly to Consult?'
    },
    { code: 'classify_duplicate', name: 'Mark as duplicate', tone: 'warning', category: 'Data quality' },
    { code: 'classify_spam', name: 'Mark as spam', tone: 'danger', category: 'Data quality' }
  ],
  engaged: [
    { code: 'qualified', name: 'Qualify lead', tone: 'primary', category: 'Qualification' },
    { code: 'disqualified', name: 'Not qualified', tone: 'warning', category: 'Qualification' },
    { code: 'stopped_responding', name: 'No response', tone: 'warning', category: 'Follow-up' },
    { code: 'classify_duplicate', name: 'Mark as duplicate', tone: 'warning', category: 'Data quality' },
    { code: 'classify_spam', name: 'Mark as spam', tone: 'danger', category: 'Data quality' }
  ],
  consult: [
    { code: 'first_offer_sent', name: 'Offer sent', tone: 'primary', category: 'Sales', note_required: true, note_placeholder: 'https://story.altezza.travel/clients/12345' },
    { code: 'disqualified', name: 'Not qualified', tone: 'warning', category: 'Qualification' },
    { code: 'stopped_responding', name: 'No response', tone: 'warning', category: 'Follow-up' },
    { code: 'classify_duplicate', name: 'Mark as duplicate', tone: 'warning', category: 'Data quality' }
  ],
  offer_negotiation: [
    { code: 'invoice_created', name: 'Create invoice', tone: 'primary', category: 'Sales' },
    { code: 'offer_declined', name: 'Offer declined', tone: 'warning', category: 'Sales' },
    { code: 'stopped_responding', name: 'No response', tone: 'warning', category: 'Follow-up' },
    { code: 'classify_duplicate', name: 'Mark as duplicate', tone: 'warning', category: 'Data quality' }
  ],
  invoice_negotiation: [
    { code: 'deposit_paid', name: 'Deposit paid', tone: 'primary', category: 'Payment' },
    { code: 'offer_declined', name: 'Offer declined', tone: 'warning', category: 'Sales' },
    { code: 'stopped_responding', name: 'No response', tone: 'warning', category: 'Follow-up' },
    { code: 'classify_duplicate', name: 'Mark as duplicate', tone: 'warning', category: 'Data quality' }
  ],
  nurture: [
    {
      code: 'move_to_consult',
      name: 'Move to Consult',
      category: 'Manual stage change',
      note_label: 'Reason',
      note_placeholder: 'Why should this lead move to Consult?'
    },
    {
      code: 'move_to_offer_negotiation',
      name: 'Move to Offer Negotiation',
      category: 'Manual stage change',
      note_label: 'Reason',
      note_placeholder: 'Why should this lead move to Offer Negotiation?'
    },
    {
      code: 'move_to_invoice_negotiation',
      name: 'Move to Invoice Negotiation',
      category: 'Manual stage change',
      note_label: 'Reason',
      note_placeholder: 'Why should this lead move to Invoice Negotiation?'
    }
  ],
  deposit_paid: [
    { code: 'full_payment_received', name: 'Full payment received', tone: 'primary', category: 'Payment' },
    { code: 'postponed', name: 'Postpone lead', tone: 'warning', category: 'Follow-up' }
  ],
  postponed: [
    { code: 'full_payment_received', name: 'Full payment received', tone: 'primary', category: 'Payment' },
    { code: 'cancelled', name: 'Cancel lead', tone: 'danger', category: 'Cancellation' }
  ],
  full_paid: [
    { code: 'completed', name: 'Mark completed', tone: 'primary', category: 'Completion' },
    { code: 'cancelled', name: 'Cancel lead', tone: 'danger', category: 'Cancellation' }
  ]
}

const EXCEPTIONAL_ACTIONS = ['classify_duplicate', 'classify_spam']

export default {
  name: 'LeadFlowActions',
  components: {
    LeadFlowActionForm,
    LeadFlowActionMenu
  },
  props: {
    leadId: {
      type: Number,
      required: true
    },
    communicationMethods: {
      type: Array,
      default: () => []
    },
    refreshKey: {
      type: Number,
      default: 0
    }
  },
  data() {
    return {
      flow: {
        current_stage: null,
        available_actions: [],
        decision: null
      },
      loaded: false,
      loading: false,
      error: null,
      requestGeneration: 0,
      contextGeneration: 0,
      selectedAction: null,
      saving: false,
      fieldErrors: {},
      formError: '',
      now: Date.now(),
      decisionTimer: null
    }
  },
  computed: {
    visibleActions() {
      const stageCode = this.flow.current_stage && this.flow.current_stage.code
      const backendActions = new Map(
        (this.flow.available_actions || []).map(action => [action.code, action])
      )
      return (ACTIONS[stageCode] || []).map(presentation => {
        const backendAction = backendActions.get(presentation.code)
        if (!backendAction) return null
        return {
          ...backendAction,
          ...presentation,
          fields: backendAction.fields || {},
          target_stage: backendAction.target_stage || null,
          requires_confirmation: Boolean(backendAction.requires_confirmation)
        }
      }).filter(Boolean)
    },
    workflowActions() {
      return this.visibleActions.filter(action => !EXCEPTIONAL_ACTIONS.includes(action.code))
    },
    decisionActions() {
      if (this.decisionExpired) return []
      return this.workflowActions
    },
    isDecisionStage() {
      return Boolean(this.flow.current_stage && this.flow.current_stage.code === 'new_lead')
    },
    isNurtureStage() {
      return Boolean(this.flow.current_stage && this.flow.current_stage.code === 'nurture')
    },
    decisionDeadline() {
      const value = this.flow.decision && this.flow.decision.deadline_at
      const timestamp = value ? Date.parse(value) : NaN
      return Number.isNaN(timestamp) ? null : timestamp
    },
    decisionExpired() {
      return this.decisionDeadline !== null && this.now >= this.decisionDeadline
    },
    decisionTimerLabel() {
      if (this.decisionDeadline === null) return '--:--'
      const remaining = Math.max(0, Math.ceil((this.decisionDeadline - this.now) / 1000))
      const minutes = Math.floor(remaining / 60)
      const seconds = remaining % 60
      return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`
    },
    decisionStateLabel() {
      if (this.decisionDeadline === null) return 'Decision timer unavailable'
      return this.decisionExpired ? 'Decision time expired' : 'Time remaining'
    },
    recommendedAction() {
      return this.workflowActions[0] || null
    },
    exceptionalActions() {
      return this.visibleActions.filter(action => EXCEPTIONAL_ACTIONS.includes(action.code))
    },
    selectedActionUsesForm() {
      if (!this.selectedAction) return false
      if (!this.isDecisionStage) return true
      return ![
        'start_first_contact',
        'start_first_contact_manually',
        'fast_track_to_consult'
      ].includes(
        this.selectedAction.code
      )
    }
  },
  watch: {
    leadId: {
      immediate: true,
      handler() {
        this.reset()
        this.loadFlow()
      }
    },
    refreshKey() {
      this.loadFlow()
    }
  },
  mounted() {
    this.decisionTimer = setInterval(() => {
      this.now = Date.now()
    }, 1000)
  },
  beforeDestroy() {
    clearInterval(this.decisionTimer)
    this.requestGeneration += 1
    this.contextGeneration += 1
  },
  methods: {
    async loadFlow() {
      const leadId = this.leadId
      const generation = this.requestGeneration + 1
      this.requestGeneration = generation
      this.loading = true
      this.error = null
      try {
        const { data } = await getLeadFlow(leadId)
        if (generation !== this.requestGeneration || leadId !== this.leadId) return
        this.flow = data || {
          current_stage: null,
          available_actions: []
        }
        this.loaded = true
        this.$emit('flow-loaded', this.flow)
      } catch (error) {
        if (generation !== this.requestGeneration || leadId !== this.leadId) return
        this.error = error
      } finally {
        if (generation === this.requestGeneration && leadId === this.leadId) {
          this.loading = false
        }
      }
    },
    selectAction(action) {
      this.selectedAction = action
      this.fieldErrors = {}
      this.formError = ''
    },
    selectDecisionAction(action) {
      if (!this.isDecisionStage) {
        this.selectAction(action)
        return
      }
      this.selectedAction = action
      this.fieldErrors = {}
      this.formError = ''
      this.submitAction({})
    },
    cancelAction() {
      if (this.saving) return
      this.selectedAction = null
      this.fieldErrors = {}
      this.formError = ''
    },
    async submitAction(payload) {
      if (!this.selectedAction || this.saving) return
      const context = createLeadContext(this.leadId, this.contextGeneration)
      if (!context.leadId) return
      const action = this.selectedAction
      const previousStage = this.flow.current_stage
      this.saving = true
      this.fieldErrors = {}
      this.formError = ''
      this.$emit('pending-change', true)
      try {
        const { data } = await postLeadFlowAction(context.leadId, action.code, payload)
        if (!isActiveLeadContext(context, this.leadId, this.contextGeneration)) return
        this.selectedAction = null
        await this.loadFlow()
        if (!isActiveLeadContext(context, this.leadId, this.contextGeneration)) return
        const change = data || {}
        this.$emit('action-complete', {
          ...change,
          previous_stage: previousStage || change.previous_stage,
          current_stage: change.current_stage || this.flow.current_stage
        })
        this.$notify({ type: 'success', title: 'Workflow updated' })
      } catch (error) {
        if (!isActiveLeadContext(context, this.leadId, this.contextGeneration)) return
        const response = error.response || {}
        const status = response.status
        const data = response.data || {}
        const decisionExpired = status === 400 && data.details === (
          'The Make Decision time expired and the lead was moved to Prepare Offer automatically.'
        )
        if (status === 409 || decisionExpired) {
          this.selectedAction = null
          await this.loadFlow()
          this.$emit('action-conflict', {
            ...data,
            previous_stage: previousStage,
            current_stage: this.flow.current_stage
          })
          let conflictTitle = 'Lead was changed in another session'
          if (decisionExpired) {
            conflictTitle =
              'Decision time expired. The lead was moved to Prepare Offer automatically.'
          }
          this.$notify({
            type: 'info',
            title: conflictTitle
          })
        } else if ([403, 404].includes(status)) {
          this.selectedAction = null
          this.$emit('access-error', error)
          this.$notify({ type: 'error', title: 'This workflow action is no longer available' })
        } else {
          this.applyActionError(data)
        }
      } finally {
        if (isActiveLeadContext(context, this.leadId, this.contextGeneration)) {
          this.saving = false
          this.$emit('pending-change', false)
        }
      }
    },
    applyActionError(data) {
      const message = data.details || data.message || 'Unable to apply this workflow action.'
      const field = {
        reason_code: 'reason',
        reason: 'reason',
        communication_method: 'communicationMethod',
        note: 'note'
      }[data.field]
      if (field) {
        this.fieldErrors = {
          [field]: message
        }
      } else {
        this.formError = message
      }
    },
    reset() {
      this.requestGeneration += 1
      this.contextGeneration += 1
      this.flow = {
        current_stage: null,
        available_actions: [],
        decision: null
      }
      this.loaded = false
      this.loading = false
      this.error = null
      this.selectedAction = null
      this.saving = false
      this.fieldErrors = {}
      this.formError = ''
      this.now = Date.now()
    }
  }
}
</script>

<style lang="scss" scoped>
.lead-flow-actions {
  min-width: 0;
  margin: 20px 24px 0;
  padding: 16px 17px;
  border: 1px solid #dfe4dd;
  border-radius: 7px;
  background: #fff;
}

.lead-flow-actions__bar {
  display: flex;
  align-items: center;
  gap: 28px;
}

.lead-flow-actions__heading {
  display: flex;
  align-items: center;
  align-self: center;
  flex: 0 0 auto;
}

.lead-flow-actions__heading p {
  margin: 0;
  color: #667675;
  font-size: 10px;
  font-weight: 800;
  line-height: 1;
  letter-spacing: .08em;
  text-transform: uppercase;
}

.lead-flow-actions__stage {
  display: inline-flex;
  align-items: center;
  align-self: center;
  flex: 0 0 auto;
  padding: 4px 8px;
  border: 1px solid;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 700;
  line-height: 1;
}

.lead-flow-actions__controls {
  display: flex;
  align-items: center;
  flex: 1 1 auto;
  gap: 18px;
  min-width: 0;
}

.lead-flow-actions__decision {
  display: grid;
  min-width: 0;
  flex: 1 1 auto;
  grid-template-columns: minmax(220px, .75fr) minmax(300px, 1.25fr);
  align-items: center;
  gap: $ui-space-20;
  padding: $ui-space-16;
  border: 1px solid $ui-line-strong;
  border-left: 4px solid $ui-amber;
  border-radius: $ui-panel-radius;
  background: $ui-amber-soft;
}

.lead-flow-actions__decision--expired {
  border-left-color: $ui-red;
  background: $ui-red-soft;
}

.lead-flow-actions__decision-summary,
.lead-flow-actions__decision-timer {
  display: grid;
  min-width: 0;
}

.lead-flow-actions__decision-summary {
  gap: $ui-space-8;
}

.lead-flow-actions__decision-heading {
  display: flex;
  min-width: 0;
  align-items: center;
  flex-wrap: wrap;
  gap: $ui-space-8;
}

.lead-flow-actions__decision-heading > span {
  color: $ui-muted;
  font-size: $ui-font-size-metadata;
  font-weight: 600;
}

.lead-flow-actions__decision-timer {
  grid-template-columns: auto minmax(0, 1fr);
  align-items: baseline;
  gap: $ui-space-12;
}

.lead-flow-actions__decision-timer strong {
  color: $ui-ink;
  font-family: $ui-font-heading;
  font-size: 30px;
  font-weight: 600;
  line-height: 1;
  font-variant-numeric: tabular-nums;
}

.lead-flow-actions__decision-timer small {
  overflow: hidden;
  color: $ui-muted;
  font-size: $ui-font-size-body;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.lead-flow-actions__decision-actions {
  display: flex;
  min-width: 0;
  align-items: center;
  justify-content: flex-end;
  flex-wrap: wrap;
  gap: $ui-space-8;
}

.lead-flow-actions__decision-action {
  width: auto;
  min-width: 0;
  min-height: $ui-control-height;
  padding-right: $ui-space-12;
  padding-left: $ui-space-12;
  white-space: nowrap;
}

.lead-flow-actions__fast-track-icon {
  width: 17px;
  height: 17px;
  flex: 0 0 17px;
  stroke: currentColor;
  stroke-width: 1.7;
  stroke-linecap: round;
  stroke-linejoin: round;
}

.lead-flow-actions__decision-error {
  grid-column: 1 / -1;
  margin: 0;
  color: $ui-red;
  font-size: $ui-font-size-body;
}

.lead-flow-actions__recommended {
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 0;
}

.lead-flow-actions__inline-actions {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
  min-width: 0;
}

.lead-flow-actions__recommended small {
  color: #899594;
  font-size: 12px;
  line-height: 1;
  white-space: nowrap;
}

.lead-flow-actions__recommended button {
  width: auto;
  min-width: 0;
  min-height: 40px;
  padding: 0 17px;
  border: 1px solid #6fa60f;
  border-radius: 6px;
  background: #77b017;
  color: #fff;
  font-size: 12px;
  font-weight: 700;
  text-align: center;
  cursor: pointer;
  transition: background-color .15s ease, border-color .15s ease;
}

.lead-flow-actions__recommended button:hover,
.lead-flow-actions__recommended button:focus-visible {
  border-color: #5f9209;
  background: #68a00c;
}

.lead-flow-actions__menus {
  display: flex;
  align-items: center;
  align-self: center;
  margin-left: auto;
  gap: 8px;
}

.lead-flow-actions__direction {
  display: inline-flex;
  align-items: center;
  align-self: center;
  color: #899594;
  font-size: 19px;
  line-height: 1;
}

.lead-flow-actions__empty,
.lead-flow-actions__state {
  margin: 0;
  color: #7e8c8b;
  font-size: 11px;
  line-height: 1;
}

.lead-flow-actions__state button {
  padding: 0;
  border: 0;
  background: transparent;
  color: #55940a;
  font-weight: 700;
  cursor: pointer;
}

.lead-flow-actions__loading {
  display: flex;
  gap: 10px;
  flex: 1 1 auto;
}

.lead-flow-actions__loading span {
  width: 220px;
  height: 40px;
  border-radius: 6px;
  background: #eef1ec;
}

.lead-flow-actions__loading span:last-child {
  width: 130px;
}

@media (max-width: 1400px) {
  .lead-flow-actions__decision {
    grid-template-columns: 1fr;
  }

  .lead-flow-actions__decision-actions {
    justify-content: flex-start;
  }
}

@media (max-width: 900px) {
  .lead-flow-actions {
    margin-right: 14px;
    margin-left: 14px;
  }

  .lead-flow-actions__bar {
    align-items: stretch;
    flex-direction: column;
    gap: 12px;
  }

  .lead-flow-actions__controls {
    align-items: stretch;
    flex-direction: column;
    gap: 10px;
  }

  .lead-flow-actions__decision-action {
    max-width: 100%;
  }

  .lead-flow-actions__decision-actions ::v-deep .lead-flow-action-menu {
    max-width: 100%;
  }

  .lead-flow-actions__decision-actions ::v-deep .lead-flow-action-menu__trigger {
    max-width: 100%;
  }

  .lead-flow-actions__direction {
    display: none;
  }

  .lead-flow-actions__recommended {
    align-items: stretch;
    flex-direction: column;
    gap: 5px;
  }

  .lead-flow-actions__recommended button {
    width: 100%;
    min-width: 0;
  }

  .lead-flow-actions__menus {
    justify-content: space-between;
    margin-left: 0;
  }
}

@media (max-width: 520px) {
  .lead-flow-actions__decision-action,
  .lead-flow-actions__decision-actions ::v-deep .lead-flow-action-menu,
  .lead-flow-actions__decision-actions ::v-deep .lead-flow-action-menu__trigger {
    width: 100%;
  }
}
</style>
